import React, { useState } from 'react';
import { Tooltip } from 'antd';
import { LinkOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import CategoryIcon from './CategoryIcon';
import { useNavigate } from 'react-router-dom';
import { Tool, AvailabilityCN, ComplianceTier } from '../types';
import { useLanguage } from '../store/LanguageContext';
import {
  availabilityCNLabels,
  availabilityCNTooltips,
  availabilityCNBadge,
} from '../constants/availabilityCN';
import { getToolNameByLanguage, getToolShortDescriptionByLanguage } from '../utils/toolLocale';
import { getToolLogoUrl, getToolLogoFallbackUrl, getToolShowcaseUrl, isPlaceholderTool } from '../utils/toolAssets';
import { getCategoryLabel } from '../utils/categoryLocale';
import { toolHasVerifiedStats } from '../utils/toolStats';
import { getDisplayTags } from '../utils/tagLocale';

interface ToolCardProps {
  tool: Tool;
}

const pricingStyleBase: Record<string, { bg: string; color: string; key: string }> = {
  free:     { bg: '#dcfce7', color: '#16a34a', key: 'pricing.free' },
  freemium: { bg: '#dbeafe', color: '#1d4ed8', key: 'pricing.freemium' },
  paid:     { bg: '#ffedd5', color: '#c2410c', key: 'pricing.paid' },
  contact:  { bg: '#f3e8ff', color: '#7e22ce', key: 'pricing.contact' },
};

/* 可用性徽章：引用集中常量 */
const getAvailBadge = (key: AvailabilityCN) => ({
  color: availabilityCNBadge[key]?.color || '#f3f4f6',
  label: availabilityCNLabels[key] || '未知',
  emoji: availabilityCNBadge[key]?.emoji || '❓',
});

const tierBadge: Record<ComplianceTier, { color: string; label: string } | null> = {
  T1_full:    { color: '#dcfce7', label: 'T1' },
  T2_partial: { color: '#dbeafe', label: 'T2' },
  T3_none:    { color: '#f3f4f6', label: 'T3' },
  pending:    null,
};

const ToolCard: React.FC<ToolCardProps> = ({ tool }) => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const localizedName = getToolNameByLanguage(tool, language);
  const localizedDescription = getToolShortDescriptionByLanguage(tool, language);
  const [logoErr, setLogoErr] = useState(false);
  const [showcaseErr, setShowcaseErr] = useState(false);

  const catName = (cat: typeof tool.category) => getCategoryLabel(cat, language);

  const tags = getDisplayTags(tool.tags, language, 3);

  const pricing = pricingStyleBase[tool.pricing_type] || pricingStyleBase.contact;
  const resolvedShowcase = getToolShowcaseUrl(tool);
  const resolvedLogo = getToolLogoUrl(tool);
  const storedLogo = getToolLogoFallbackUrl(tool);
  const hasShowcase = !!resolvedShowcase && !showcaseErr;
  const hasLogo = !logoErr;
  const isPlaceholder = isPlaceholderTool(tool);
  const showStats = toolHasVerifiedStats(tool);
  // Skip remote favicon CDNs (Google blocked in CN); fall through to category icon.
  const remoteFavicon = '';

  return (
    <div className="tc-card" onClick={() => navigate(`/tools/${tool.slug}`)}>
      {/* ── 顶部：展示图 or 干净 logo 封面 ── */}
      <div className="tc-cover">
        {hasShowcase ? (
          <img
            src={resolvedShowcase}
            alt={localizedName}
            className="tc-cover-img"
            loading="lazy"
            onError={() => setShowcaseErr(true)}
          />
        ) : (
          <div className="tc-cover-placeholder tc-cover-placeholder-clean">
            {hasLogo ? (
              <img
                src={resolvedLogo}
                alt={localizedName}
                className="tc-placeholder-logo"
                onError={(e) => {
                  const img = e.currentTarget;
                  const step = Number(img.dataset.fallback || '0');
                  if (step < 1 && storedLogo && storedLogo !== img.src) {
                    img.dataset.fallback = '1';
                    img.src = storedLogo;
                    return;
                  }
                  if (step < 2 && remoteFavicon && remoteFavicon !== img.src) {
                    img.dataset.fallback = '2';
                    img.src = remoteFavicon;
                    return;
                  }
                  setLogoErr(true);
                }}
              />
            ) : (
              <span style={{ fontSize: 40, color: '#64748b' }}>
                <CategoryIcon name={tool.category?.icon} />
              </span>
            )}
          </div>
        )}
        {/* Freemium is the default for most cards — hide it to reduce badge noise */}
        {tool.pricing_type && tool.pricing_type !== 'freemium' && (
          <span className="tc-pricing-badge" style={{ background: pricing.bg, color: pricing.color }}>
            {t(pricing.key)}
          </span>
        )}
        {tool.benefit_tier === 'premium' && (
          <span className="tc-benefit-badge tc-benefit-premium">Premium</span>
        )}
        {tool.benefit_tier === 'standard' && (
          <span className="tc-benefit-badge tc-benefit-standard">Verified</span>
        )}
        {/* 有截图时才叠 logo，避免无截图时 logo 重复 */}
        {hasShowcase && (
          <div className="tc-logo-overlay">
            {hasLogo ? (
              <Tooltip title={isPlaceholder ? (language === 'zh-CN' ? '该工具图标暂缺' : 'Logo missing') : undefined}>
                <img
                  src={resolvedLogo}
                  alt={localizedName}
                  className="tc-logo-img"
                  onError={(e) => {
                    const img = e.currentTarget;
                    const step = Number(img.dataset.fallback || '0');
                    if (step < 1 && storedLogo && storedLogo !== img.src) {
                      img.dataset.fallback = '1';
                      img.src = storedLogo;
                      return;
                    }
                    if (step < 2 && remoteFavicon && remoteFavicon !== img.src) {
                      img.dataset.fallback = '2';
                      img.src = remoteFavicon;
                      return;
                    }
                    setLogoErr(true);
                  }}
                />
              </Tooltip>
            ) : (
              <span style={{ fontSize: 18, lineHeight: 1 }}><CategoryIcon name={tool.category?.icon} /></span>
            )}
          </div>
        )}
      </div>

      {/* ── 卡片主体 ── */}
      <div className="tc-body">
        {/* 标题 + 分类行 */}
        <div className="tc-title-row">
          <h3 className="tc-title">{localizedName}</h3>
        </div>

        <div className="tc-meta-row">
          {tool.category && (
            <span className="tc-cat-chip">
              {tool.category.icon && <CategoryIcon name={tool.category.icon} style={{ marginRight: 3 }} />}
              {catName(tool.category)}
            </span>
          )}
          {tool.availability_cn && tool.availability_cn !== 'unknown' && (() => {
            const badge = getAvailBadge(tool.availability_cn);
            return (
              <Tooltip title={availabilityCNTooltips[tool.availability_cn] || badge.label}>
                <span className="tc-avail-chip" style={{ background: badge.color }}>
                  {badge.emoji}
                </span>
              </Tooltip>
            );
          })()}
          {tool.compliance_tier && tierBadge[tool.compliance_tier] && (
            <Tooltip title={t('detail.complianceTooltip', { tier: tierBadge[tool.compliance_tier]!.label })}>
              <span className="tc-tier-chip" style={{ background: tierBadge[tool.compliance_tier]!.color }}>
                <SafetyCertificateOutlined style={{ fontSize: 10, marginRight: 2 }} />
                {tierBadge[tool.compliance_tier]!.label}
              </span>
            </Tooltip>
          )}
        </div>

        {/* 描述 */}
        <p className="tc-desc">{localizedDescription}</p>

        {/* hashtag 标签 */}
        {tags.length > 0 && (
          <div className="tc-tags">
            {tags.slice(0, 3).map((tag, i) => (
              <span key={i} className="tc-tag">#{tag}</span>
            ))}
          </div>
        )}

        {/* 底部：评分 + 浏览 + 访问按钮 */}
        <div className="tc-footer">
          <div className="tc-stats">
            {showStats ? (
              <span className="tc-rating">
                {Array.from({ length: 5 }).map((_, i) => (
                  <span
                    key={i}
                    style={{ color: i < Math.round(Number(tool.rating)) ? '#f59e0b' : '#d1d5db', fontSize: 12 }}
                  >★</span>
                ))}
                <strong className="tc-rating-num">{Number(tool.rating).toFixed(1)}</strong>
              </span>
            ) : null}
            {showStats && Number(tool.view_count) > 0 && (
              <span className="tc-views">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: 3, verticalAlign: 'middle' }}><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                {tool.view_count > 1000 ? `${(tool.view_count / 1000).toFixed(1)}k` : tool.view_count}
              </span>
            )}
          </div>
          <a
            href={tool.website_url}
            target="_blank"
            rel="noopener noreferrer"
            className="tc-visit-btn"
            onClick={(e) => e.stopPropagation()}
            title={t('detail.visitSite')}
          >
            <LinkOutlined style={{ fontSize: 13 }} />
          </a>
        </div>
      </div>
    </div>
  );
};

export default ToolCard;
